import React, { useEffect, useState } from "react";
import {
  Typography,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  CircularProgress, // Import CircularProgress
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  getAnsServNotificationPreferences,
  updateAnsServNotificationPreferences,
} from "apiHandler/actions";
import { useUserData } from "components/customHooks/userDataContext";
import useSnackbar from "components/customHooks/useSnackbar";

const Settings = () => {
  const [expanded, setExpanded] = useState([]);
  const [formValues, setFormValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const { CustomSnackbar, setSnackbarMessage } = useSnackbar();
  const userData = useUserData();
  const token = userData?.access_token;

  useEffect(() => {
    getNotificationPreferences();
  }, []);

  const getNotificationPreferences = async () => {
    try {
      const payload = {
        type: "AnsweringService",
        practice_id: `${userData.selected_practice}`,
      };
      const res = await getAnsServNotificationPreferences(payload, token);

      setFormValues(res.map((item) => ({ ...item })));
      setLoading(false); // Set loading to false once data is fetched
    } catch (error) {
      console.log("error: ", error);
      setLoading(false); // Set loading to false if there's an error
    }
  };

  const handleInputChange = (id, field, value) => {
    setFormValues((prev) => {
      const updatedValues = prev.map((item) => {
        if (item.id === id) {
          if (
            (field === "voice_reminder_opt_time_start" &&
              value === item.voice_reminder_opt_time_end) ||
            (field === "voice_reminder_opt_time_end" &&
              value === item.voice_reminder_opt_time_start)
          ) {
            setSnackbarMessage("Start time and end time cannot be the same.");
            return { ...item }; // return item without changing it
          }
          return { ...item, [field]: value }; // update field value
        }
        return item;
      });
      return updatedValues;
    });
  };

  const handleSave = async () => {
    setDisabled(true);
    try {
      const payload = {
        type: "AnsweringService",
        practice_id: `${userData.selected_practice}`,
        updated_data: formValues,
      };
      await updateAnsServNotificationPreferences(payload, token);
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setDisabled(false);
    }
  };

  const generateTimeSlots = () => {
    const timeSlots = [];
    for (let hour = 0; hour < 24; hour++) {
      const formattedTime = `${String(hour).padStart(2, "0")}:00`;
      timeSlots.push(formattedTime);
    }
    return timeSlots;
  };

  const timeSlots = generateTimeSlots();

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(
      (prev) =>
        isExpanded
          ? [...prev, panel] // Add panel if expanding
          : prev.filter((p) => p !== panel) // Remove panel if collapsing
    );
  };

  return (
    <Box style={{ padding: "20px", margin: "auto" }}>
      <CustomSnackbar />
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h5" fontSize={18} gutterBottom>
          Answering Service
        </Typography>
        <Button
          variant="contained"
          sx={{ backgroundColor: "#cb3366" }}
          onClick={handleSave}
          disabled={disabled}
        >
          Save
        </Button>
      </Box>

      {/* Show loader while data is being fetched */}
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="300px"
        >
          <CircularProgress />
        </Box>
      ) : (
        formValues.map((item) => (
          <Accordion
            key={item.id}
            expanded={expanded.includes(`panel${item.id}`)}
            onChange={handleChange(`panel${item.id}`)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`panel${item.id}a-content`}
              id={`panel${item.id}a-header`}
            >
              <Typography>
                {item.contact_name} - {item.category}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" alignItems="start" gap={8}>
                <TextField
                  label="Name"
                  value={item.contact_name}
                  onChange={(e) =>
                    handleInputChange(item.id, "contact_name", e.target.value)
                  }
                  margin="normal"
                  variant="outlined"
                  disabled={disabled}
                />
                <TextField
                  label="Phone"
                  value={item.mobile}
                  onChange={(e) =>
                    handleInputChange(item.id, "mobile", e.target.value)
                  }
                  margin="normal"
                  variant="outlined"
                  disabled={disabled}
                />
                <FormControl component="fieldset" margin="normal">
                  <FormLabel component="legend">SMS Notification</FormLabel>
                  <RadioGroup
                    row
                    value={item.sms_notification}
                    onChange={(e) =>
                      handleInputChange(
                        item.id,
                        "sms_notification",
                        e.target.value
                      )
                    }
                    name={`sms-notification-${item.id}`}
                  >
                    <FormControlLabel
                      value="Yes"
                      control={<Radio />}
                      label="Yes"
                      disabled={disabled}
                    />
                    <FormControlLabel
                      value="No"
                      control={<Radio />}
                      label="No"
                      disabled={disabled}
                    />
                  </RadioGroup>
                </FormControl>
                <FormControl component="fieldset" margin="normal">
                  <FormLabel component="legend">Voice Reminder</FormLabel>
                  <RadioGroup
                    row
                    value={item.voice_reminder_opt_in}
                    onChange={(e) =>
                      handleInputChange(
                        item.id,
                        "voice_reminder_opt_in",
                        e.target.value
                      )
                    }
                    name={`voice-reminder-${item.id}`}
                  >
                    <FormControlLabel
                      value="Yes"
                      control={<Radio />}
                      label="Yes"
                      disabled={disabled}
                    />
                    <FormControlLabel
                      value="No"
                      control={<Radio />}
                      label="No"
                      disabled={disabled}
                    />
                  </RadioGroup>
                </FormControl>
                <Box>
                  <Typography variant="body1" gutterBottom>
                    Voice Reminder Timer
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Select
                      value={item.voice_reminder_opt_time_start}
                      onChange={(e) =>
                        handleInputChange(
                          item.id,
                          "voice_reminder_opt_time_start",
                          e.target.value
                        )
                      }
                      variant="outlined"
                      disabled={disabled}
                    >
                      {timeSlots.map((time) => (
                        <MenuItem key={time} value={time}>
                          {time}
                        </MenuItem>
                      ))}
                    </Select>
                    <Select
                      value={item.voice_reminder_opt_time_end}
                      onChange={(e) =>
                        handleInputChange(
                          item.id,
                          "voice_reminder_opt_time_end",
                          e.target.value
                        )
                      }
                      variant="outlined"
                      disabled={disabled}
                    >
                      {timeSlots.map((time) => (
                        <MenuItem key={time} value={time}>
                          {time}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Box>
  );
};

export default Settings;
